import { useEffect, useMemo, useState, type ReactNode } from "react";

import { t } from "@/i18n";

export interface ColumnDef<T> {
  key: string;
  header: string;
  accessor: (row: T) => ReactNode;
  sortValue?: (row: T) => number | string;
  /** Sayisal sutunlar icin sag hizalama — hucreye `.num` sinifi eklenir (index.css: `.tbl .num` sag hizalar, ust seviye `.num` tabular font verir). */
  align?: "right";
}

interface SortableTableProps<T> {
  columns: ColumnDef<T>[];
  rows: T[];
  rowKey: (row: T) => string | number;
  initialSortKey?: string;
  /**
   * Legacy `modals.js` `columns.rowClass(row)` karsiligi — satira ek Tailwind
   * sinifi ekler (ör. SLA rengi). Geriye uyumlu: verilmezse davranis
   * degismez (KPI detay modali "eşitleme" görevi).
   */
  rowClassName?: (row: T) => string | undefined;
  /** Mobilde yatay kaydirmada ilk sutun (ör. urun adi) sabit kalir — `sticky left-0` + sag golge. */
  stickyFirstColumn?: boolean;
  /** Verilirse yalniz ilk N satir gosterilir, altta "Daha fazla goster (kalan M)" dugmesi (uzun listeler sayfayi sisirmesin). */
  pageSize?: number;
}

const STICKY_CLASS = "sticky left-0 z-[1] bg-card shadow-[4px_0_8px_-6px_rgba(15,23,42,0.25)]";

/** Siralanabilir tablo — mobilde yatay kaydirma ipucu (docs/arayuz-tasarim-rehberi.md §3). */
export function SortableTable<T>({
  columns,
  rows,
  rowKey,
  initialSortKey,
  rowClassName,
  stickyFirstColumn = false,
  pageSize,
}: SortableTableProps<T>) {
  const [sortKey, setSortKey] = useState<string | null>(initialSortKey ?? null);
  const [direction, setDirection] = useState<"asc" | "desc">("asc");
  const [limit, setLimit] = useState<number | null>(pageSize ?? null);

  // Satir kumesi degisince (filtre/sekme) sayfalama basa doner.
  useEffect(() => {
    setLimit(pageSize ?? null);
  }, [rows, pageSize]);

  const sortedRows = useMemo(() => {
    const column = columns.find((c) => c.key === sortKey);
    if (!column?.sortValue) return rows;
    const withValues = rows.map((row) => ({ row, value: column.sortValue!(row) }));
    withValues.sort((a, b) => {
      if (a.value < b.value) return direction === "asc" ? -1 : 1;
      if (a.value > b.value) return direction === "asc" ? 1 : -1;
      return 0;
    });
    return withValues.map((w) => w.row);
  }, [rows, sortKey, direction, columns]);

  function toggleSort(key: string) {
    if (sortKey === key) {
      setDirection((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setDirection("asc");
    }
  }

  if (rows.length === 0) {
    return <p className="text-sm text-muted">{t("table.empty")}</p>;
  }

  return (
    <div>
      <p className="mb-1 text-xs text-muted lg:hidden">{t("table.scroll_hint")}</p>
      <div className="overflow-x-auto rounded-lg border-[1.5px] border-line">
        <table className="tbl w-full min-w-[480px]">
          <thead>
            <tr>
              {columns.map((column, i) => (
                <th
                  key={column.key}
                  scope="col"
                  className={`${column.align === "right" ? "text-right" : ""} ${i === 0 && stickyFirstColumn ? STICKY_CLASS : ""}`}
                >
                  {column.sortValue ? (
                    // ARAYUZ-DENETIM DUZELTMESI (madde 2): min-h-[44px] TEK
                    // BASINA yetmiyordu — kisa basliklarda (ör. "Ad") genislik
                    // metin kadar dar kalip dokunma alani 44x44'ten kucuk
                    // kaliyordu. min-w-[44px] + px-2 (-ml-2 ile th'nin kendi
                    // px-3'unu telafi ederek gorsel hizalamayi bozmadan) ile
                    // genislik de garanti edilir; whitespace-nowrap KALDIRILDI
                    // (th'de degil, ustte) boylece uzun basliklar sarabilir.
                    <button
                      type="button"
                      onClick={() => toggleSort(column.key)}
                      className="-ml-2 inline-flex min-h-[44px] min-w-[44px] items-center gap-1 whitespace-normal px-2 text-left font-semibold"
                      aria-sort={sortKey === column.key ? (direction === "asc" ? "ascending" : "descending") : "none"}
                    >
                      {column.header}
                      {sortKey === column.key && (
                        <span aria-hidden="true">{direction === "asc" ? "▲" : "▼"}</span>
                      )}
                    </button>
                  ) : (
                    <span className="whitespace-nowrap">{column.header}</span>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {(limit != null ? sortedRows.slice(0, limit) : sortedRows).map((row) => (
              <tr key={rowKey(row)} className={rowClassName?.(row) ?? undefined}>
                {columns.map((column, i) => (
                  <td
                    key={column.key}
                    className={`whitespace-nowrap ${column.align === "right" ? "num" : ""} ${i === 0 && stickyFirstColumn ? STICKY_CLASS : ""}`}
                  >
                    {column.accessor(row)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {limit != null && sortedRows.length > limit && (
        <div className="mt-2 flex items-center justify-between gap-2 text-xs text-muted">
          <span>{t("table.gosterilen", { n: String(limit), toplam: String(sortedRows.length) })}</span>
          <button type="button" onClick={() => setLimit((l) => (l ?? 0) + (pageSize ?? 25))} className="btn btn-outline btn-sm">
            {t("table.daha_fazla", { n: String(Math.min(pageSize ?? 25, sortedRows.length - limit)) })}
          </button>
        </div>
      )}
    </div>
  );
}
